import {
  Link,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useQueries, useQuery } from "@tanstack/react-query";
import { fetchAllStars, fetchBossAllStars } from "../api/allStars";
import { EQ_CLASS_NAMES } from "../api/eqClasses";
import { fetchServerProgressionOverview } from "../api/progression";
import { ErrorState } from "../components/ErrorState";
import { LoadingState } from "../components/LoadingState";
import { RankingsSectionNav } from "../components/rankings/RankingsSectionNav";
import type {
  AllStarStandingSummary,
  AllStarsResponse,
  ProgressionBossRequirement,
  ProgressionExpansionSummary,
  ProgressionTierSummary,
} from "../api/types";
import {
  getActiveServerSlug,
  serverAllStarsPath,
  serverCharacterPath,
  serverGuildPath,
} from "../utils/siteContext";

function displayExpansionName(expansion: ProgressionExpansionSummary) {
  return expansion.name === "Original EverQuest" ? "Classic" : expansion.name;
}

function tiersFor(expansion: ProgressionExpansionSummary) {
  return [...(expansion.tiers ?? expansion.raidTiers ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
  );
}

function objectivesFor(tier: ProgressionTierSummary) {
  return [...(tier.objectives ?? tier.raidObjectives ?? [])].sort(
    (a, b) =>
      (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name),
  );
}

function bossesFor(tier: ProgressionTierSummary) {
  const unique = new Map<string, ProgressionBossRequirement>();
  for (const objective of objectivesFor(tier)) {
    for (const boss of objective.requiredBosses ?? objective.bosses ?? []) {
      const id = String(bossId(boss) ?? bossSlug(boss) ?? bossName(boss));
      if (!unique.has(id)) unique.set(id, boss);
    }
  }
  return [...unique.values()];
}

function bossesForExpansion(expansion: ProgressionExpansionSummary | null) {
  if (!expansion) return [];
  const unique = new Map<string, ProgressionBossRequirement>();
  for (const tier of tiersFor(expansion)) {
    for (const boss of bossesFor(tier)) {
      const key = String(bossId(boss) ?? bossSlug(boss) ?? bossName(boss));
      if (!unique.has(key)) unique.set(key, boss);
    }
  }
  return [...unique.values()];
}

function bossId(requirement: ProgressionBossRequirement) {
  return (
    requirement.raidBossId ??
    requirement.raidBoss?.id ??
    requirement.boss?.id ??
    requirement.id ??
    null
  );
}

function bossSlug(requirement: ProgressionBossRequirement) {
  return (
    requirement.raidBoss?.slug ??
    requirement.boss?.slug ??
    requirement.slug ??
    null
  );
}

function bossName(requirement: ProgressionBossRequirement) {
  return (
    requirement.raidBoss?.name ??
    requirement.boss?.name ??
    requirement.name ??
    "Unknown boss"
  );
}

function bossZone(requirement: ProgressionBossRequirement) {
  return (
    requirement.raidBoss?.raidZone?.name ??
    requirement.boss?.raidZone?.name ??
    requirement.zone ??
    null
  );
}

function rankingPartitionsFor(expansion: ProgressionExpansionSummary | null) {
  if (!expansion) return [];
  return tiersFor(expansion).flatMap((tier) =>
    (tier.rankingPartitions ?? []).map((partition) => ({
      ...partition,
      tier,
      slug: partition.slug || tier.slug,
    })),
  );
}

function rankingPartitionsFromRaidContent(raidContent: unknown) {
  const rows = ((raidContent as { rankingPartitions?: Array<{ rankingPartitionId?: string; id?: string; name: string; slug?: string | null; isActive?: boolean }> } | null)?.rankingPartitions ?? [])
    .filter((entry) => entry.isActive !== false)
    .map((entry) => ({
      id: entry.rankingPartitionId ?? entry.id ?? "",
      name: entry.name,
      slug: entry.slug ?? String(entry.rankingPartitionId ?? entry.id ?? ""),
      isDefault: false,
    }))
    .filter((entry) => entry.id);
  const deduped = new Map<string, (typeof rows)[number]>();
  for (const row of rows) deduped.set(String(row.id), row);
  return [...deduped.values()].sort((a, b) => a.name.localeCompare(b.name));
}

function selectedPartitionFor(
  expansion: ProgressionExpansionSummary | null,
  partitionSlug: string | undefined,
  raidContent?: unknown,
) {
  const partitions = rankingPartitionsFor(expansion).length > 0
    ? rankingPartitionsFor(expansion)
    : rankingPartitionsFromRaidContent(raidContent);
  return (
    partitions.find((partition) => partition.slug === partitionSlug) ??
    partitions.find((partition) => partition.isDefault) ??
    partitions[0] ??
    null
  );
}

function partitionHasTier(
  partition: ReturnType<typeof selectedPartitionFor>,
): partition is NonNullable<ReturnType<typeof selectedPartitionFor>> & { tier: ProgressionTierSummary } {
  return Boolean(partition && "tier" in partition);
}

function metricFromCategory(
  category: string | undefined,
): "DAMAGE" | "HEALING" {
  return category?.toLowerCase() === "healing" ? "HEALING" : "DAMAGE";
}

function categoryFromMetric(metric: "DAMAGE" | "HEALING") {
  return metric === "HEALING" ? "healing" : "damage";
}

function formatPoints(value: number | null | undefined) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "0.0";
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
}

function classShorthand(className: string | null | undefined) {
  if (!className) return "???";
  return (
    className
      .replace(/[^A-Za-z]/g, "")
      .slice(0, 3)
      .toUpperCase() || "???"
  );
}

function percentileValue(standing: AllStarStandingSummary) {
  return standing.percentile ?? standing.averagePercentile ?? null;
}

function formatPercentile(value: number | null | undefined) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "-";
  return value.toLocaleString(undefined, {
    maximumFractionDigits: value % 1 === 0 ? 0 : 1,
  });
}

function percentileBadgeClass(value: number | null | undefined) {
  if (typeof value !== "number" || !Number.isFinite(value))
    return "percentile-badge percentile-badge--none";
  if (value >= 100) return "percentile-badge percentile-badge--gold";
  if (value >= 99) return "percentile-badge percentile-badge--pink";
  if (value >= 95) return "percentile-badge percentile-badge--orange";
  if (value >= 75) return "percentile-badge percentile-badge--purple";
  if (value >= 50) return "percentile-badge percentile-badge--blue";
  if (value >= 25) return "percentile-badge percentile-badge--green";
  return "percentile-badge percentile-badge--gray";
}

function rankBadgeClass(rank: number | null | undefined) {
  if (rank === 1) return "rank-badge rank-badge--gold";
  if (rank === 2) return "rank-badge rank-badge--silver";
  if (rank === 3) return "rank-badge rank-badge--bronze";
  return "rank-badge rank-badge--plain";
}

function updatedLabel(response: AllStarsResponse | undefined) {
  const date = response?.snapshot?.snapshotDate;
  if (!date) return "Updated: Pending";
  return `Updated ${new Date(date).toLocaleDateString()}`;
}

function withClassFilter(path: string, className: string | null | undefined) {
  const parts = path.split("?")[0].split("/");
  const tail = parts[parts.length - 1] ?? "";
  const hasClassTail = EQ_CLASS_NAMES.includes(tail as (typeof EQ_CLASS_NAMES)[number]);
  if (hasClassTail) parts.pop();
  const base = parts.join("/");
  return className ? `${base}/${encodeURIComponent(className)}` : base;
}

function AllStarTable({
  title,
  data,
  allHref,
  isLoading,
  error,
  activeServerSlug,
  classHrefBase,
  activeClassName,
}: {
  title: string;
  data?: AllStarsResponse;
  allHref?: string;
  isLoading?: boolean;
  error?: unknown;
  activeServerSlug: string;
  classHrefBase: string;
  activeClassName?: string;
}) {
  const standings = data?.standings ?? [];

  return (
    <section className="all-stars-table-section">
      <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
        <h2 className="h4 mb-0">{title}</h2>
        {allHref ? (
          <Link
            className="small link-secondary"
            to={withClassFilter(allHref, activeClassName)}
          >
            All
          </Link>
        ) : null}
      </div>
      <div className="card border-secondary-subtle bg-body-tertiary rankings-card rankings-table-card">
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-dark table-sm table-hover align-middle mb-0 rankings-mini-table all-stars-rankings-table">
              <thead>
                <tr>
                  <th className="text-body-secondary" style={{ width: "4rem" }}>
                    Rank
                  </th>
                  <th>Character</th>
                  <th>Class</th>
                  <th>Guild</th>
                  <th className="text-end">Percentile</th>
                  <th className="text-end">Points</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="text-body-secondary py-3">
                      Loading standings...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={6} className="text-danger py-3">
                      Could not load standings.
                    </td>
                  </tr>
                ) : standings.length > 0 ? (
                  standings.map((standing: AllStarStandingSummary) => {
                    const percentile = percentileValue(standing);

                    return (
                      <tr key={standing.id}>
                        <td>
                          <span className={rankBadgeClass(standing.rank)}>
                            #{standing.rank}
                          </span>
                        </td>
                        <td>
                          <Link
                            className="link-light fw-semibold text-decoration-none"
                            to={serverCharacterPath(
                              activeServerSlug,
                              standing.character,
                            )}
                          >
                            {standing.character?.name ?? "Unknown player"}
                          </Link>
                        </td>
                        <td>
                          {standing.character?.className ? (
                            <Link
                              className="class-shorthand text-decoration-none"
                              to={withClassFilter(
                                classHrefBase,
                                standing.character.className,
                              )}
                            >
                              {classShorthand(standing.character.className)}
                            </Link>
                          ) : (
                            <span className="class-shorthand">???</span>
                          )}
                        </td>
                        <td>
                          {standing.guild ? (
                            <Link
                              className="link-light text-decoration-none"
                              to={serverGuildPath(
                                activeServerSlug,
                                standing.guild,
                              )}
                            >
                              {standing.guild.name}
                            </Link>
                          ) : (
                            <span className="text-body-secondary">-</span>
                          )}
                        </td>
                        <td className="text-end tabular-nums">
                          <span className={percentileBadgeClass(percentile)}>
                            {formatPercentile(percentile)}
                          </span>
                        </td>
                        <td className="text-end tabular-nums">
                          {formatPoints(
                            standing.allStarPoints ??
                              standing.averagePercentile,
                          )}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="text-body-secondary py-3">
                      No All-Star standings yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

export function AllStarsPage() {
  const {
    expansionSlug,
    partitionSlug,
    bossSlug = "all",
    category,
    pageNum,
    classSlug,
  } = useParams<{
    expansionSlug?: string;
    partitionSlug?: string;
    bossSlug?: string;
    category?: string;
    pageNum?: string;
    classSlug?: string;
  }>();
  const navigate = useNavigate();
  const selectedClassFilter = classSlug ?? "all";
  const selectedClassName = EQ_CLASS_NAMES.includes(
    selectedClassFilter as (typeof EQ_CLASS_NAMES)[number],
  )
    ? selectedClassFilter
    : undefined;
  const serverSlug = getActiveServerSlug();
  const metric = metricFromCategory(category);
  const page = Math.max(1, Number(pageNum ?? "1") || 1);
  const isFullList = Boolean(pageNum);

  const progressionQuery = useQuery({
    queryKey: ["progression-race-board", serverSlug],
    queryFn: () => fetchServerProgressionOverview(serverSlug),
    enabled: serverSlug.length > 0,
  });

  const progression = progressionQuery.data?.progression;
  const server = progressionQuery.data?.server;
  const expansions = [...(progression?.expansions ?? [])].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
  );
  const selectedExpansion =
    expansions.find((expansion) => expansion.slug === expansionSlug) ??
    expansions[0] ??
    null;
  const selectedPartition = selectedPartitionFor(
    selectedExpansion,
    partitionSlug,
    progressionQuery.data?.raidContent,
  );
  const selectedTier =
    (partitionHasTier(selectedPartition) ? selectedPartition.tier : null) ??
    (selectedExpansion ? tiersFor(selectedExpansion)[0] : null);
  const bosses = bossesForExpansion(selectedExpansion);
  const selectedBoss =
    bossSlug === "all"
      ? null
      : bosses.find((boss) => bossSlug === bossSlugForPath(boss));
  const selectedBossId = selectedBoss ? bossId(selectedBoss) : null;
  const selectedBossSlug = selectedBoss ? bossSlugForPath(selectedBoss) : "all";
  const serverId =
    typeof server?.id === "number" ? server.id : Number(server?.id);
  const rankingPartitionId = selectedPartition?.id
    ? String(selectedPartition.id)
    : undefined;
  const selectedExpansionSlug =
    selectedExpansion?.slug ?? expansionSlug ?? "classic";
  const selectedExpansionId =
    selectedExpansion?.id ? String(selectedExpansion.id) : undefined;
  const selectedPartitionSlug =
    selectedPartition?.slug ?? partitionSlug ?? "default";
  const categorySlug = categoryFromMetric(metric);

  const canonicalPath =
    selectedExpansion && selectedPartition
      ? serverAllStarsPath(serverSlug, {
          expansionSlug: selectedExpansionSlug,
          partitionSlug: selectedPartitionSlug,
          bossSlug: selectedBossSlug,
          metric,
          all: isFullList,
          pageNum: page,
        })
      : null;

  const overallQuery = useQuery({
    queryKey: [
      "all-stars",
      "overall",
      serverId,
      rankingPartitionId,
      metric,
      selectedClassName,
      isFullList,
      page,
    ],
    queryFn: () =>
      fetchAllStars({
        serverId,
        rankingPartitionId,
        expansionId: selectedExpansionId,
        metric,
        className: selectedClassName,
        limit: isFullList && !selectedBossId ? 50 : 10,
        page: isFullList && !selectedBossId ? page : 1,
      }),
    enabled: Boolean(
      serverId && rankingPartitionId && (!isFullList || !selectedBossId),
    ),
  });

  const bossFullQuery = useQuery({
    queryKey: [
      "all-stars",
      "boss-full",
      serverId,
      rankingPartitionId,
      selectedBossId,
      metric,
      selectedClassName,
      page,
    ],
    queryFn: () =>
      fetchBossAllStars(String(selectedBossId), {
        serverId,
        rankingPartitionId,
        expansionId: selectedExpansionId,
        metric,
        className: selectedClassName,
        limit: 50,
        page,
      }),
    enabled: Boolean(
      serverId && rankingPartitionId && selectedBossId && isFullList,
    ),
  });

  const bossQueries = useQueries({
    queries: !isFullList
      ? bosses.flatMap((boss) => {
          const id = bossId(boss);
          if (!id) return [];
          return [
            {
              queryKey: [
                "all-stars",
                "boss",
                serverId,
                rankingPartitionId,
                id,
                metric,
                selectedClassName,
              ],
              queryFn: () =>
                fetchBossAllStars(String(id), {
                serverId,
                rankingPartitionId,
                expansionId: selectedExpansionId,
                metric,
                  className: selectedClassName,
                  limit: 10,
                }),
              enabled: Boolean(serverId && rankingPartitionId),
            },
          ];
        })
      : [],
  });

  const navigateTo = (next: {
    expansionSlug?: string;
    partitionSlug?: string;
    bossSlug?: string;
    metric?: "DAMAGE" | "HEALING";
    classSlug?: string | null;
  }) => {
    navigate(
      serverAllStarsPath(serverSlug, {
        expansionSlug: next.expansionSlug ?? selectedExpansionSlug,
        partitionSlug: next.partitionSlug ?? selectedPartitionSlug,
        bossSlug: next.bossSlug ?? selectedBossSlug,
        metric: next.metric ?? metric,
        classSlug: next.classSlug === undefined ? (selectedClassName ?? null) : next.classSlug,
      }),
    );
  };

  return (
    <>
      <section className="page-hero rankings-hero">
        <div className="content-wrapper py-4">
          <h1 className="display-6 fw-semibold mb-0">
            All-Stars
            <span className="page-hero__server d-block">
              {server?.name ?? "Server"}
            </span>
          </h1>
          <p className="fs-3 mb-0 text-body-secondary mt-2">
            {selectedBoss
              ? `${bossName(selectedBoss)}${bossZone(selectedBoss) ? ` · ${bossZone(selectedBoss)}` : ""}`
              : "All-Star Rankings"}
          </p>
        </div>
        {expansions.length > 0 ? (
          <div className="page-subnav rankings-expansion-subnav">
            <div className="content-wrapper">
              <ul
                className="nav secondary-tabs"
                aria-label="All-Stars expansion scope"
              >
                {expansions.map((expansion) => {
                    const partition = selectedPartitionFor(expansion, undefined, progressionQuery.data?.raidContent);
                  return (
                    <li className="nav-item" key={expansion.id}>
                      <Link
                        className={`nav-link ${selectedExpansionSlug === expansion.slug ? "active" : ""}`}
                        to={serverAllStarsPath(serverSlug, {
                          expansionSlug: expansion.slug,
                          partitionSlug: partition?.slug ?? "default",
                          bossSlug: "all",
                          metric,
                        })}
                      >
                        {displayExpansionName(expansion)}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        ) : null}
        <RankingsSectionNav
          activeSection="allstars"
          serverSlug={serverSlug}
          progressionExpansionSlug={selectedExpansion?.slug}
          allStarsPartitionSlug={selectedPartitionSlug}
          allStarsMetric={metric}
        />
      </section>

      <div className="page-main">
        <div className="content-wrapper">
          {progressionQuery.isLoading ? (
            <LoadingState label="Loading All-Stars" />
          ) : null}
          {progressionQuery.isError ? (
            <ErrorState
              title="Could not load All-Stars"
              error={progressionQuery.error}
            />
          ) : null}

          {progressionQuery.data && selectedExpansion && selectedPartition ? (
            <>
              {canonicalPath && canonicalPath !== window.location.pathname
                ? null
                : null}
              <section
                className="resource-grid-controls mb-4"
                aria-label="All-Stars filters"
              >
                <div className="resource-grid-controls__header">
                  <div>
                    <h3>Filters</h3>
                  </div>
                  <div className="small text-body-secondary text-nowrap">
                    {updatedLabel(overallQuery.data ?? bossFullQuery.data)}
                  </div>
                </div>
                <div className="resource-grid-controls__fields rankings-filter-grid">
                  <label className="resource-grid-control">
                    <span>Partition</span>
                    <select
                      className="form-select form-select-sm"
                      value={selectedPartitionSlug}
                      onChange={(event) =>
                        navigateTo({
                          partitionSlug: event.target.value,
                        })
                      }
                    >
                      {(rankingPartitionsFor(selectedExpansion).length > 0
                        ? rankingPartitionsFor(selectedExpansion)
                        : rankingPartitionsFromRaidContent(progressionQuery.data?.raidContent)
                      ).map(
                        (partition) => (
                          <option value={partition.slug ?? ""} key={partition.id}>
                            {partition.name}
                          </option>
                        ),
                      )}
                    </select>
                  </label>
                  <label className="resource-grid-control">
                    <span>Metric</span>
                    <select
                      className="form-select form-select-sm"
                      value={metric}
                      onChange={(event) =>
                        navigateTo({
                          metric:
                            event.target.value === "HEALING"
                              ? "HEALING"
                              : "DAMAGE",
                        })
                      }
                    >
                      <option value="DAMAGE">Damage</option>
                      <option value="HEALING">Healing</option>
                    </select>
                  </label>
                  <label className="resource-grid-control">
                    <span>Class</span>
                    <select
                      className="form-select form-select-sm"
                      value={selectedClassName ?? "all"}
                      onChange={(event) => {
                        const value = event.target.value;
                        navigateTo({ classSlug: value === "all" ? null : value });
                      }}
                    >
                      <option value="all">All Classes</option>
                      {EQ_CLASS_NAMES.map((className) => (
                        <option key={className} value={className}>
                          {className}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </section>

              {isFullList ? (
                <>
                  <AllStarTable
                    activeServerSlug={serverSlug}
                    classHrefBase={serverAllStarsPath(serverSlug, {
                      expansionSlug: selectedExpansionSlug,
                      partitionSlug: selectedPartitionSlug,
                      bossSlug: selectedBossSlug,
                      metric,
                      all: true,
                      pageNum: 1,
                      classSlug: selectedClassName ?? null,
                    })}
                    activeClassName={selectedClassName}
                    title={
                      selectedBoss
                        ? bossName(selectedBoss)
                        : `${selectedPartition.name} Overall`
                    }
                    data={selectedBoss ? bossFullQuery.data : overallQuery.data}
                    isLoading={
                      selectedBoss
                        ? bossFullQuery.isLoading
                        : overallQuery.isLoading
                    }
                    error={
                      selectedBoss ? bossFullQuery.error : overallQuery.error
                    }
                  />
                  <PaginationLinks
                    serverSlug={serverSlug}
                    expansionSlug={selectedExpansionSlug}
                    partitionSlug={selectedPartitionSlug}
                    bossSlug={selectedBossSlug}
                    metric={metric}
                    page={page}
                    totalPages={
                      (selectedBoss
                        ? bossFullQuery.data?.totalPages
                        : overallQuery.data?.totalPages) ?? 1
                    }
                    className={selectedClassName}
                  />
                </>
              ) : (
                <div className="d-grid gap-3">
                  <AllStarTable
                    activeServerSlug={serverSlug}
                    classHrefBase={serverAllStarsPath(serverSlug, {
                      expansionSlug: selectedExpansionSlug,
                      partitionSlug: selectedPartitionSlug,
                      bossSlug: "all",
                      metric,
                      all: true,
                      pageNum: 1,
                      classSlug: selectedClassName ?? null,
                    })}
                    activeClassName={selectedClassName}
                    title="Overall"
                    data={overallQuery.data}
                    isLoading={overallQuery.isLoading}
                    error={overallQuery.error}
                    allHref={serverAllStarsPath(serverSlug, {
                      expansionSlug: selectedExpansionSlug,
                      partitionSlug: selectedPartitionSlug,
                      bossSlug: "all",
                      metric,
                      all: true,
                      pageNum: 1,
                      classSlug: selectedClassName ?? null,
                    })}
                  />
                  <hr className="section-separator" />
                  {bosses.map((boss, index) => {
                    const bossData = bossQueries[index];
                    return (
                      <div key={String(bossId(boss) ?? bossSlugForPath(boss))}>
                        <AllStarTable
                          activeServerSlug={serverSlug}
                          classHrefBase={serverAllStarsPath(serverSlug, {
                            expansionSlug: selectedExpansionSlug,
                            partitionSlug: selectedPartitionSlug,
                            bossSlug: bossSlugForPath(boss),
                            metric,
                            all: true,
                            pageNum: 1,
                          })}
                          activeClassName={selectedClassName}
                          title={bossName(boss)}
                          data={bossData?.data}
                          isLoading={bossData?.isLoading}
                          error={bossData?.error}
                          allHref={serverAllStarsPath(serverSlug, {
                            expansionSlug: selectedExpansionSlug,
                            partitionSlug: selectedPartitionSlug,
                            bossSlug: bossSlugForPath(boss),
                            metric,
                            all: true,
                            pageNum: 1,
                          })}
                        />
                        {index < bosses.length - 1 ? (
                          <hr className="section-separator" />
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          ) : progressionQuery.data ? (
            <div className="alert alert-secondary">
              No All-Star ranking content is configured for this server.
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

function bossSlugForPath(boss: ProgressionBossRequirement) {
  return (
    bossSlug(boss) ??
    String(bossId(boss) ?? bossName(boss))
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")
  );
}

function PaginationLinks({
  serverSlug,
  expansionSlug,
  partitionSlug,
  bossSlug,
  metric,
  page,
  totalPages,
  className,
}: {
  serverSlug: string;
  expansionSlug: string;
  partitionSlug: string;
  bossSlug: string;
  metric: "DAMAGE" | "HEALING";
  page: number;
  totalPages: number;
  className?: string;
}) {
  return (
    <div className="d-flex justify-content-between align-items-center mt-3">
      <Link
        className={`btn btn-outline-secondary btn-sm ${page <= 1 ? "disabled" : ""}`}
        to={withClassFilter(
          serverAllStarsPath(serverSlug, {
            expansionSlug,
            partitionSlug,
            bossSlug,
            metric,
            all: true,
            pageNum: Math.max(1, page - 1),
          }),
          className,
        )}
      >
        Previous
      </Link>
      <span className="small text-body-secondary">
        Page {page} of {totalPages}
      </span>
      <Link
        className={`btn btn-outline-secondary btn-sm ${page >= totalPages ? "disabled" : ""}`}
        to={withClassFilter(
          serverAllStarsPath(serverSlug, {
            expansionSlug,
            partitionSlug,
            bossSlug,
            metric,
            all: true,
            pageNum: Math.min(totalPages, page + 1),
          }),
          className,
        )}
      >
        Next
      </Link>
    </div>
  );
}
